import type { HPOTerm } from '@/components/common/hpoTypes'
import { hpoAPIClient } from '@/features/terminology/hpo/HPOAPIClient'
import { extractHPOId, isHpoCode } from '@/features/terminology/hpo/hpoTypes'
import * as Sentry from '@sentry/react'
import { useEffect, useMemo, useState } from 'react'

const resolvedNames = new Map<string, string>()

/**
 * Display names for HPO terms whose `name` is still a raw HP code. IMDB keeps
 * only the code, so comparator phenotypes read back from a placed order arrive
 * code-only. The input terms are never mutated; only the returned copies carry
 * the resolved name.
 */
export function useResolvedHpoTermNames(terms: HPOTerm[]): HPOTerm[] {
  const [, setResolvedCount] = useState(resolvedNames.size)
  const pendingKey = terms
    .filter((t) => isHpoCode(t.name) && !resolvedNames.has(t.id))
    .map((t) => t.id)
    .join(',')

  useEffect(() => {
    if (!pendingKey) return
    let cancelled = false
    const resolve = async () => {
      for (const id of pendingKey.split(',')) {
        try {
          const node = await hpoAPIClient.getHPOTermById(extractHPOId(id))
          if (node?.lbl && !isHpoCode(node.lbl)) resolvedNames.set(id, node.lbl)
        } catch (error) {
          Sentry.captureException(error, { tags: { operation: 'comparator.hpo-label.resolve' } })
        }
      }
      if (!cancelled) setResolvedCount(resolvedNames.size)
    }
    void resolve()
    return () => {
      cancelled = true
    }
  }, [pendingKey])

  return useMemo(
    () =>
      terms.map((t) => (isHpoCode(t.name) && resolvedNames.has(t.id) ? { ...t, name: resolvedNames.get(t.id)! } : t)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [terms, pendingKey, resolvedNames.size]
  )
}
